import { useState } from 'react'
import {
  Button,
  IconButton,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  Stack,
} from '@mui/material'
import { useAgreement, useDownloadAttachment } from '../queries'
import { AgreementAttachmentFile } from '../types'
import { AddAttachmentModal } from './AddAttachmentModal'
import AddIcon from '@mui/icons-material/Add'
import { useTranslation } from 'react-i18next'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded'
import { intuitiveFromNow } from '@cibo/ui'
import { useSnackbar } from 'notistack'
import { Can, AuthUserPermission } from '@cibo/profile'

type AttachmentsTableProps = {
  agreementId: string
}

export const AttachmentsTable = ({ agreementId }: AttachmentsTableProps) => {
  const { t } = useTranslation('impact/Agreements/Attachments')
  const [attachmentModal, setAttachmentModal] = useState<{ open: boolean }>({
    open: false,
  })

  const agreement = useAgreement(agreementId)

  const { enqueueSnackbar } = useSnackbar()
  const onError = () => {
    enqueueSnackbar(t('downloadError'), { variant: 'error' })
  }
  const { openFile } = useDownloadAttachment(onError)

  return (
    <>
      <Can useAny={[AuthUserPermission.AGREEMENTS_ATTACHMENTS_WRITE]}>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="h5">{t('Attachments')}</Typography>

          <Button
            size="small"
            onClick={() => setAttachmentModal({ open: true })}
            startIcon={<AddIcon />}
          >
            {t('uploadAttachment')}
          </Button>
        </Stack>
      </Can>

      {!!agreement.data?.files.length && (
        <TableContainer component={Paper}>
          <Table aria-label="attachments" size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('File')}</TableCell>
                <TableCell align="right">{t('Updated')}</TableCell>
                <TableCell align="right">{t('Download')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {agreement.data?.files.map((row: AgreementAttachmentFile) => (
                <TableRow key={row.fileId}>
                  <TableCell component="th" scope="row">
                    <Stack>
                      <Typography>{row.fileName || row.fileData.fileName}</Typography>
                      {!!row.description && (
                        <Typography variant="caption">
                          {t(`attachmentType_${row.description}`)}
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell align="right">{intuitiveFromNow(row.fileData.modifiedDate)}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      edge="end"
                      aria-label={t('download')}
                      onClick={openFile(agreementId, row.fileId)}
                      data-testid="download-attachment-button"
                    >
                      <DownloadRoundedIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <AddAttachmentModal
        agreementId={agreementId}
        {...attachmentModal}
        onRequestClose={() => setAttachmentModal({ open: false })}
      />
    </>
  )
}
